import api from './api'
import type {
  AccrualsResponse,
  AllocationMonthReadinessNotesUpdateRequest,
  AllocationMonthReadinessResponse,
  AllocationMonthReadinessUpdateRequest,
  NetIncomeRangeDriftResponse,
  AllocationCreate,
  AllocationListResponse,
  AllocationProposeRequest,
  AllocationProposalResponse,
  AllocationResponse,
  CapTableResponse,
  CapitalAccountsResponse,
  DailyThresholdResponse,
  ProjectedThresholdResponse,
  DistributionCreate,
  DistributionListResponse,
  DistributionResponse,
  ObligationsResponse,
  ProposeRequest,
  ProposalResponse,
  ThresholdResponse,
  VestingCatchUpCreate,
  VestingResponse,
  WaterfallAgreementMeta,
  WaterfallConfigItem,
  WaterfallSettingsResponse,
  CapitalContributionsConfig,
  CapitalContributionsSyncPreviewResponse,
  EquityTieOutResponse,
  PrimeRateSyncResponse,
  WaterfallJournalEntryGenerateRequest,
  WaterfallJournalEntryGenerateResponse,
  WaterfallJournalEntryStatus,
  WaterfallJeMonthsSummaryResponse,
  WaterfallJeMode,
  WaterfallDeleteQboResponse,
  WaterfallPushQboUpdateRequest,
  WaterfallPushQboUpdateResponse,
  PacYearEndCloseGenerateRequest,
  PacYearEndCloseGenerateResponse,
  PacYearEndCloseStatusResponse,
} from '@/types/waterfall'

export async function proposeDistribution(body: ProposeRequest): Promise<ProposalResponse> {
  const response = await api.post<ProposalResponse>('/waterfall/propose', body)
  return response.data
}

export async function getDistributionHistory(): Promise<DistributionListResponse> {
  const response = await api.get<DistributionListResponse>('/waterfall/history')
  return response.data
}

export async function logDistribution(body: DistributionCreate): Promise<DistributionResponse> {
  const response = await api.post<DistributionResponse>('/waterfall/history', body)
  return response.data
}

export async function updateDistributionNote(
  distributionId: number,
  note: string | null,
): Promise<DistributionResponse> {
  const response = await api.patch<DistributionResponse>(`/waterfall/history/${distributionId}`, {
    note,
  })
  return response.data
}

export async function getAccruals(): Promise<AccrualsResponse> {
  const response = await api.get<AccrualsResponse>('/waterfall/accruals')
  return response.data
}

export async function getCapTable(asof: string): Promise<CapTableResponse> {
  const response = await api.get<CapTableResponse>('/waterfall/cap-table', {
    params: { asof },
  })
  return response.data
}

export async function getVestingSchedule(asof?: string): Promise<VestingResponse> {
  const response = await api.get<VestingResponse>('/waterfall/vesting', {
    params: asof ? { asof } : undefined,
  })
  return response.data
}

export async function logStandaloneCatchUp(
  body: VestingCatchUpCreate,
): Promise<DistributionResponse> {
  const response = await api.post<DistributionResponse>('/waterfall/vesting/catch-up', body)
  return response.data
}

export async function getThreshold(asof: string): Promise<ThresholdResponse> {
  const response = await api.get<ThresholdResponse>('/waterfall/threshold', {
    params: { asof },
  })
  return response.data
}

export async function getDailyThreshold(params: {
  end: string
  start?: string
}): Promise<DailyThresholdResponse> {
  const response = await api.get<DailyThresholdResponse>('/waterfall/threshold/daily', {
    params,
  })
  return response.data
}

export async function getProjectedThreshold(params: {
  case_id?: number
} = {}): Promise<ProjectedThresholdResponse> {
  const response = await api.get<ProjectedThresholdResponse>('/waterfall/threshold/projected', {
    params,
  })
  return response.data
}

export async function getObligations(asof: string): Promise<ObligationsResponse> {
  const response = await api.get<ObligationsResponse>('/waterfall/obligations', {
    params: { asof },
  })
  return response.data
}

export async function getWaterfallAgreement(): Promise<Blob> {
  const response = await api.get<Blob>('/waterfall/agreement', {
    responseType: 'blob',
    params: { t: Date.now() },
    headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
  })
  return response.data
}

export async function getWaterfallAgreementMeta(): Promise<WaterfallAgreementMeta> {
  const response = await api.get<WaterfallAgreementMeta>('/waterfall/agreement/meta')
  return response.data
}

export async function uploadWaterfallAgreement(file: File): Promise<WaterfallAgreementMeta> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await api.post<WaterfallAgreementMeta>('/waterfall/agreement', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export async function resetWaterfallAgreement(): Promise<WaterfallAgreementMeta> {
  const response = await api.delete<WaterfallAgreementMeta>('/waterfall/agreement')
  return response.data
}

export async function getWaterfallSettings(): Promise<WaterfallSettingsResponse> {
  const response = await api.get<WaterfallSettingsResponse>('/waterfall/settings')
  return response.data
}

export async function updateWaterfallSetting(
  key: string,
  value: unknown,
): Promise<WaterfallSettingsResponse> {
  const response = await api.put<WaterfallSettingsResponse>(`/waterfall/settings/${key}`, {
    value,
  })
  return response.data
}

export async function syncPrimeRateSchedule(): Promise<PrimeRateSyncResponse> {
  const response = await api.post<PrimeRateSyncResponse>('/waterfall/settings/prime-rate/sync')
  return response.data
}

export async function previewCapitalContributionsSync(): Promise<CapitalContributionsSyncPreviewResponse> {
  const response = await api.post<CapitalContributionsSyncPreviewResponse>(
    '/waterfall/settings/capital-contributions/sync-preview',
  )
  return response.data
}

export async function applyCapitalContributionsSync(
  value: CapitalContributionsConfig,
): Promise<WaterfallSettingsResponse> {
  const response = await api.post<WaterfallSettingsResponse>(
    '/waterfall/settings/capital-contributions/sync-apply',
    { value },
  )
  return response.data
}

export async function fetchEquityTieOut(asof?: string): Promise<EquityTieOutResponse> {
  const response = await api.get<EquityTieOutResponse>('/waterfall/allocations/equity-tieout', {
    params: asof ? { asof } : undefined,
  })
  const data = response.data
  return {
    ...data,
    engine_total_equity: Number(data.engine_total_equity),
    qbo_total_equity: Number(data.qbo_total_equity),
    difference: Number(data.difference),
  }
}

export async function proposeAllocation(
  body: AllocationProposeRequest,
): Promise<AllocationProposalResponse> {
  const response = await api.post<AllocationProposalResponse>('/waterfall/allocations/propose', body)
  return response.data
}

export async function getAllocations(): Promise<AllocationListResponse> {
  const response = await api.get<AllocationListResponse>('/waterfall/allocations')
  return response.data
}

export async function logAllocation(body: AllocationCreate): Promise<AllocationResponse> {
  const response = await api.post<AllocationResponse>('/waterfall/allocations', body)
  return response.data
}

export async function deleteLatestAllocation(): Promise<void> {
  await api.delete('/waterfall/allocations/latest')
}

export async function getCapitalAccounts(asof: string): Promise<CapitalAccountsResponse> {
  const response = await api.get<CapitalAccountsResponse>('/waterfall/allocations/capital-accounts', {
    params: { asof },
  })
  return response.data
}

export async function getWaterfallJournalEntryMonthsSummary(
  mode: WaterfallJeMode,
): Promise<WaterfallJeMonthsSummaryResponse> {
  const response = await api.get<WaterfallJeMonthsSummaryResponse>(
    '/waterfall/journal-entries/months-summary',
    { params: { mode } },
  )
  return response.data
}

export type WaterfallJournalEntryStatusParams = {
  month?: string
  distribution_num?: number
}

export async function getWaterfallJournalEntryStatus(
  mode: WaterfallJeMode,
  params: WaterfallJournalEntryStatusParams,
): Promise<WaterfallJournalEntryStatus> {
  const response = await api.get<WaterfallJournalEntryStatus>('/waterfall/journal-entries', {
    params: { mode, ...params },
  })
  return response.data
}

export async function generateWaterfallJournalEntry(
  body: WaterfallJournalEntryGenerateRequest,
): Promise<WaterfallJournalEntryGenerateResponse> {
  const response = await api.post<WaterfallJournalEntryGenerateResponse>(
    '/waterfall/journal-entries/generate',
    body,
  )
  return response.data
}

export async function pushWaterfallJournalEntryQboUpdate(
  draftId: number,
  body: WaterfallPushQboUpdateRequest = {},
): Promise<WaterfallPushQboUpdateResponse> {
  const response = await api.post<WaterfallPushQboUpdateResponse>(
    `/waterfall/journal-entries/${draftId}/push-qbo-update`,
    body,
  )
  return response.data
}

export async function deleteWaterfallJournalEntryFromQbo(
  draftId: number,
): Promise<WaterfallDeleteQboResponse> {
  const response = await api.post<WaterfallDeleteQboResponse>(
    `/waterfall/journal-entries/${draftId}/delete-qbo`,
  )
  return response.data
}

export async function getPacYearEndCloseStatus(
  month: string,
): Promise<PacYearEndCloseStatusResponse> {
  const response = await api.get<PacYearEndCloseStatusResponse>(
    '/waterfall/journal-entries/pac-year-end-close',
    { params: { month } },
  )
  return response.data
}

export async function generatePacYearEndClose(
  body: PacYearEndCloseGenerateRequest,
): Promise<PacYearEndCloseGenerateResponse> {
  const response = await api.post<PacYearEndCloseGenerateResponse>(
    '/waterfall/journal-entries/pac-year-end-close',
    body,
  )
  return response.data
}

export async function pushPacYearEndCloseQboUpdate(
  draftId: number,
  body: WaterfallPushQboUpdateRequest = {},
): Promise<WaterfallPushQboUpdateResponse> {
  const response = await api.post<WaterfallPushQboUpdateResponse>(
    `/waterfall/journal-entries/pac-year-end-close/${draftId}/push-qbo-update`,
    body,
  )
  return response.data
}

export async function deletePacYearEndCloseFromQbo(
  draftId: number,
): Promise<WaterfallDeleteQboResponse> {
  const response = await api.post<WaterfallDeleteQboResponse>(
    `/waterfall/journal-entries/pac-year-end-close/${draftId}/delete-qbo`,
  )
  return response.data
}

export async function getAllocationMonthReadiness(
  month: string,
  options?: { includeQbo?: boolean },
): Promise<AllocationMonthReadinessResponse> {
  const response = await api.get<AllocationMonthReadinessResponse>(
    '/waterfall/allocations/month-readiness',
    { params: { month, include_qbo: options?.includeQbo ?? false } },
  )
  return response.data
}

export async function updateAllocationMonthReadiness(
  body: AllocationMonthReadinessUpdateRequest,
): Promise<AllocationMonthReadinessResponse> {
  const response = await api.put<AllocationMonthReadinessResponse>(
    '/waterfall/allocations/month-readiness',
    body,
  )
  return response.data
}

export async function updateAllocationMonthNotes(
  month: string,
  notes: string,
): Promise<AllocationMonthReadinessResponse> {
  const body: AllocationMonthReadinessNotesUpdateRequest = { month, notes }
  const response = await api.patch<AllocationMonthReadinessResponse>(
    '/waterfall/allocations/month-readiness/notes',
    body,
  )
  return response.data
}

export async function getNetIncomeRangeDrift(): Promise<NetIncomeRangeDriftResponse> {
  const response = await api.get<NetIncomeRangeDriftResponse>(
    '/waterfall/allocations/net-income-drift',
  )
  return response.data
}

export type { WaterfallAgreementMeta, WaterfallConfigItem, PrimeRateSyncResponse }
